import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { MeteorObservable } from 'meteor-rxjs';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import { debounceTime, shareReplay } from 'rxjs/operators';
import { defaultCancelReasons } from 'server/helpers/defaultCancelReasons';
import { WithRequiredProperty } from 'server/helpers/withRequiredProperty';
import { EmploymentUserLogs } from 'server/shared/collections/employmentUserLogs';
import { Users } from 'server/shared/collections/users';
import { userIncludesRequiredFieldsForEmployment } from 'server/shared/helpers/canBookInviteeForJob';
import { CancelReason } from 'server/shared/models/cancelReason';
import { Customer } from 'server/shared/models/customer';
import { DeclineReasonEnum } from 'server/shared/models/declineReason.enum';
import { Employment } from 'server/shared/models/employment';
import { EmploymentInvitee } from 'server/shared/models/employmentInvitee';
import {
  EmploymentInviteeStatusEnum,
  EmploymentInviteeStatusReason,
} from 'server/shared/models/employmentInviteeStatusEnum';
import { RoleEnum } from 'server/shared/models/role.enum';
import { EmploymentAvailability, PoolerUser } from 'server/shared/models/user';
import { filterIsNotEmpty } from '../../../helpers/filterIsNotEmpty';
import { PreparedEmploymentInvitee } from '../../../types/employment/PreparedEmploymentInvitee';
import { EmploymentUserLogsBottomSheetComponent } from '../../employments/employment-user-logs-bottom-sheet/employment-user-logs-bottom-sheet.component';
import { CancelDialog } from '../../jobs/job-edit/dialogs/cancel-dialog/cancel-dialog.component';
import { DeclineInviteeDialog } from '../../jobs/job-edit/dialogs/decline-invitee-dialog/decline-invitee-dialog.component';
import { CustomerService } from '../../services/customer.service';
import { EmploymentService } from '../../services/employment.service';
import { ToastService } from '../../services/toast/toast.service';
import { UserService } from '../../services/user.service';
import {
  RequiredFieldsDialog,
  RequiredFieldsDialogUpdatedFields,
} from '../required-fields-dialog/required-fields.component';

export enum InviteeActionTheme {
  DEFAULT = 'DEFAULT',
  SUCCESS_ICON = 'SUCCESS_ICON',
  DANGER_ICON = 'DANGER_ICON',
  YELLOW_ICON = 'YELLOW_ICON',
  FULL_DANGER = 'FULL_DANGER',
}

export type InviteeAction = {
  label: string;
  icon: string;
  cypressName: string;
  theme: InviteeActionTheme;
  run: () => void;
};

@Component({
  selector: 'app-employment-invitee-list-item',
  templateUrl: './employment-invitee-list-item.component.html',
  styleUrls: ['./employment-invitee-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmploymentInviteeListItemComponent implements OnInit, OnDestroy {
  destroyed$ = new Subject<void>();

  userLogsSubscription: Subscription | undefined;

  @Input() employment?: Employment;
  @Input() preventDirectAccept?: boolean;
  @Input() loggedInCustomer?: Customer;

  invitee$ = new BehaviorSubject<PreparedEmploymentInvitee | undefined>(
    undefined,
  );

  @Input() set invitee(invitee: PreparedEmploymentInvitee | undefined) {
    this.invitee$.next(invitee);
  }

  get invitee(): PreparedEmploymentInvitee | undefined {
    return this.invitee$.getValue();
  }

  @Input() fromDate?: Date;
  @Input() toDate?: Date;
  @Input() employmentCustomer?: Customer;

  inviteeAvailabilities$ = this.invitee$.pipe(
    map(
      (invitee) =>
        invitee?.user?.employmentAvailabilities ||
        invitee?.personalData?.externalAvailabilities ||
        [],
    ),

    shareReplay(1),
  );

  availabilityNotes$ = this.inviteeAvailabilities$.pipe(
    map((availabilities) =>
      availabilities
        // filter out availabilities without notes
        .filter(
          (a): a is WithRequiredProperty<EmploymentAvailability, 'notes'> =>
            !!a.notes,
        )
        .map((availability) => ({
          start: availability.startDate,
          end: availability.endDate,
          notes: availability.notes,
        })),
    ),
    shareReplay(1),
  );

  // "forLoad" is separate so that the collapse can be animated
  detailsCollapsedForLoad = true;
  detailsCollapsed = true;

  inviteeUser?: PoolerUser;

  readyInvitee$ = this.invitee$.pipe(filter(filterIsNotEmpty));

  $showContactInfo = combineLatest([
    this.userService.readyDispo$,
    this.readyInvitee$,
  ]).pipe(
    map(([currentUser, invitee]) => {
      const isOwnPooler = currentUser.customerId === invitee.customer.id;
      const shouldShowContactInfoBasedOnStatus = [
        EmploymentInviteeStatusEnum.AVAILABLE,
        EmploymentInviteeStatusEnum.BOOKED,
        EmploymentInviteeStatusEnum.CANCELLED,
        EmploymentInviteeStatusEnum.POSSIBLE_CANDIDATE,
      ].includes(invitee.status);
      return isOwnPooler || shouldShowContactInfoBasedOnStatus;
    }),
  );

  @Input() isDisabled = false;

  @Input() isClosed = false;

  statusEnum = EmploymentInviteeStatusEnum;

  cancelReasons = defaultCancelReasons;

  @HostListener('click') onClick() {
    const newState = !this.detailsCollapsedForLoad;
    this.detailsCollapsedForLoad = newState;
    setTimeout(() => {
      this.detailsCollapsed = newState;
      this.changeDetectorRef.detectChanges();
    });
  }
  constructor(
    private ngZone: NgZone,
    private employmentService: EmploymentService,
    private toastService: ToastService,
    private matBottomSheet: MatBottomSheet,
    private changeDetectorRef: ChangeDetectorRef,
    private userService: UserService,
    private dialog: MatDialog,
    private customerService: CustomerService,
  ) {
    this.customerService.readyDispoCustomer$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((customer) => {
        this.cancelReasons = customer?.cancelReasons || defaultCancelReasons;
      });
  }

  openBottomSheet() {
    const inviteeId = this.invitee?.id;
    if (!inviteeId) {
      throw new Error('No invitee provided for employment userLogs');
    }
    this.userLogsSubscription = MeteorObservable.subscribe(
      'employmentUserLogsOfInvitee',
      inviteeId,
    ).subscribe(() => {
      EmploymentUserLogs.find({ inviteeId: inviteeId })
        .pipe(debounceTime(50), takeUntil(this.destroyed$))
        .subscribe((userLogs) => {
          const data = { userLogs, invitee: this.invitee };
          this.matBottomSheet.open(EmploymentUserLogsBottomSheetComponent, {
            data,
          });
        });
    });
  }

  allActions = {
    setToAvailable: {
      label: 'setToAvailable',
      cypressName: 'set-to-available',
      icon: 'check',
      theme: InviteeActionTheme.SUCCESS_ICON,
      run: () =>
        this.updateInviteeStatus(EmploymentInviteeStatusEnum.AVAILABLE),
    },
    setToUnAvailable: {
      label: 'setToUnavailable',
      cypressName: 'set-to-unavailable',
      icon: 'close',
      theme: InviteeActionTheme.DANGER_ICON,
      run: () =>
        this.updateInviteeStatus(EmploymentInviteeStatusEnum.UNAVAILABLE),
    },
    decline: {
      label: 'decline',
      cypressName: 'decline',
      icon: 'close-rejected-remove-circle',
      theme: InviteeActionTheme.DANGER_ICON,
      run: () => this.openDeclineInviteeDialog(),
    },
    book: {
      label: 'book',
      cypressName: 'book',
      icon: 'star-filled',
      theme: InviteeActionTheme.YELLOW_ICON,
      run: () => this.updateInviteeStatus(EmploymentInviteeStatusEnum.BOOKED),
    },
    submitPossibleCandidate: {
      label: 'submitPossibleCandidate',
      cypressName: 'submitPossibleCandidate',
      icon: 'star-check-outline',
      theme: InviteeActionTheme.SUCCESS_ICON,
      run: () =>
        this.updateInviteeStatus(
          EmploymentInviteeStatusEnum.POSSIBLE_CANDIDATE,
        ),
    },
    cancel: {
      label: 'cancelSomething',
      cypressName: 'cancel',
      icon: 'close-rejected-remove-circle',
      theme: InviteeActionTheme.FULL_DANGER,
      run: () => this.openCancelInviteeDialog(),
    },
  };
  availableActions: InviteeAction[] = [];
  inviteeUserTracker?: Tracker.Computation;

  ngOnInit(): void {
    this.availableActions = this.getAvailableActions(this.invitee);
    this.inviteeUserTracker = Tracker.autorun(() => {
      this.ngZone.run(() => {
        this.inviteeUser = Users.findOne({
          _id: this.invitee?.userId,
          role: RoleEnum.POOL,
        }) as PoolerUser;
      });
    });
  }

  getAvailableActions(invitee?: EmploymentInvitee): InviteeAction[] {
    if (!invitee) {
      return [];
    }
    const actions: InviteeAction[] = [];
    switch (invitee.status) {
      case EmploymentInviteeStatusEnum.OPEN:
        actions.push(this.allActions.setToAvailable);
        actions.push(this.allActions.setToUnAvailable);
        break;
      case EmploymentInviteeStatusEnum.AVAILABLE:
        if (
          this.employment?.transfers.find(
            (t) =>
              t.targetCustomer.id === this.loggedInCustomer?._id &&
              t.preventDirectAccept,
          )
        ) {
          actions.push(this.allActions.submitPossibleCandidate);
        } else {
          actions.push(this.allActions.book);
        }
        actions.push(this.allActions.setToUnAvailable);
        actions.push(this.allActions.decline);
        break;
      case EmploymentInviteeStatusEnum.UNAVAILABLE:
        actions.push(this.allActions.setToAvailable);
        break;
      case EmploymentInviteeStatusEnum.BOOKED:
        actions.push(this.allActions.cancel);
        break;
      case EmploymentInviteeStatusEnum.POSSIBLE_CANDIDATE:
        actions.push(this.allActions.book);
        actions.push(this.allActions.decline);
        break;
    }
    return actions;
  }

  async updateInviteeStatus(
    status: EmploymentInviteeStatusEnum,
    statusInfo?: EmploymentInviteeStatusReason,
  ) {
    if (
      [
        EmploymentInviteeStatusEnum.BOOKED,
        EmploymentInviteeStatusEnum.POSSIBLE_CANDIDATE,
      ].includes(status)
    ) {
      if (!this.employmentCustomer) {
        return;
      }
      if (
        this.inviteeUser &&
        !userIncludesRequiredFieldsForEmployment(
          this.inviteeUser,
          this.employmentCustomer,
        )
      ) {
        const dialogRef = this.dialog.open(RequiredFieldsDialog, {
          data: {
            user: this.inviteeUser,
            requiredFields: this.employmentCustomer?.requiredJobFields,
          },
        });

        dialogRef
          .afterClosed()
          .subscribe((result: RequiredFieldsDialogUpdatedFields) => {
            if (result) {
              Meteor.call(
                'updateRequiredFields',
                this.inviteeUser!._id,
                result,
                (error: Meteor.Error) => {
                  if (error) {
                    this.toastService.add({
                      type: 'ERROR',
                      message: 'serverError',
                    });
                    return;
                  } else {
                    this.updateInviteeStatus(
                      EmploymentInviteeStatusEnum.BOOKED,
                    );
                  }
                },
              );
            }
          });
        return;
      }
    }
    if (!this.employment) {
      throw new Error('missing employment');
    }
    if (!this.invitee) {
      throw new Error('missing invitee');
    }
    await this.employmentService.updateEmploymentInviteeStatus(
      this.invitee.id,
      this.employment._id,
      status,
      statusInfo,
    );
    switch (status) {
      case EmploymentInviteeStatusEnum.AVAILABLE:
        // no toast
        break;
      case EmploymentInviteeStatusEnum.UNAVAILABLE:
        // no toast
        break;
      case EmploymentInviteeStatusEnum.BOOKED:
        this.toastService.add({
          type: 'SUCCESS',
          message: 'userBookedSuccessfullyEmployment',
        });
        break;
      case EmploymentInviteeStatusEnum.POSSIBLE_CANDIDATE:
        this.toastService.add({
          type: 'SUCCESS',
          message: 'userSuccessfullySubmittedAsEmploymentPossibleCandidate',
        });
        break;
      case EmploymentInviteeStatusEnum.CANCELLED:
        this.toastService.add({
          type: 'SUCCESS',
          message: 'userCanceledSuccessfullyEmployment',
        });
        break;
      default:
        this.toastService.add({
          type: 'SUCCESS',
          message: 'saved',
        });
        break;
    }
  }

  openCancelInviteeDialog() {
    const dialogRef = this.dialog.open(CancelDialog, {
      data: {
        reasons: this.cancelReasons,
      },
    });
    dialogRef
      .afterClosed()
      .subscribe(
        (result: {
          reason: CancelReason;
          reasonDescription: string;
          informInviteeOfReason: boolean;
        }) => {
          if (result?.reason) {
            this.updateInviteeStatus(
              EmploymentInviteeStatusEnum.CANCELLED,
              result,
            );
          }
        },
      );
  }

  openDeclineInviteeDialog() {
    const dialogRef = this.dialog.open(DeclineInviteeDialog);
    dialogRef
      .afterClosed()
      .subscribe(
        (result: {
          reason: DeclineReasonEnum;
          reasonDescription: string;
          informInviteeOfReason: boolean;
        }) => {
          if (result?.reason) {
            this.updateInviteeStatus(
              EmploymentInviteeStatusEnum.DECLINED,
              result,
            );
          }
        },
      );
  }

  ngOnDestroy() {
    if (this.inviteeUserTracker) {
      this.inviteeUserTracker.stop();
    }
    if (this.userLogsSubscription) {
      this.userLogsSubscription?.unsubscribe();
    }
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  readonly inviteeActionTheme = InviteeActionTheme;
}
